import { format } from 'date-fns';
import { type FC, useState } from 'react';
import { useDispatch } from 'react-redux';

import SubmitButton from 'components/common/formikForm/SubmitButton';

import { downloadAdminAnalyticsReport } from 'api/rest/analytics';
import { downloadBillingReport } from 'api/rest/serviceProviderBilling';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import type { AppDispatch } from 'redux/store';
import type { BillingRevenueCountryCode } from 'types';
import { downloadBlob } from 'utils/file';
import { getErrorMessage } from 'utils/v2/errorHelper';

const XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const getReportFilename = (contentDisposition: string | undefined, fallback: string) => {
    const encodedFilename = contentDisposition?.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
    if (encodedFilename) {
        return decodeURIComponent(encodedFilename);
    }
    const filename = contentDisposition?.match(/filename="?([^";]+)"?/i)?.[1];
    return filename?.trim() || fallback;
};

const readBlobText = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
        reader.onerror = () => reject(reader.error);
        reader.readAsText(blob);
    });

/** Blob downloads keep JSON error bodies opaque until they are read. */
const withParsedBlobError = async (error: unknown): Promise<unknown> => {
    if (!error || typeof error !== 'object' || !('response' in error)) {
        return error;
    }
    const response = (error as { response?: { data?: unknown } }).response;
    const data = response?.data;
    if (!(data instanceof Blob)) {
        return error;
    }
    try {
        const parsed: unknown = JSON.parse(await readBlobText(data));
        return { ...error, response: { ...response, data: parsed } };
    } catch {
        return error;
    }
};

interface FinancialReportDownloadButtonProps {
    serviceProviderId: number | string | null;
    fromDate: Date | null;
    toDate: Date | null;
    hasValidDateRange: boolean;
    fullWidth?: boolean;
    /** Billing revenue sends the selected country subset; all countries stays omitted. */
    countryCodes?: BillingRevenueCountryCode[];
    report?: 'analytics' | 'serviceProviderBilling';
}

const FinancialReportDownloadButton: FC<FinancialReportDownloadButtonProps> = ({
    serviceProviderId,
    fromDate,
    toDate,
    hasValidDateRange,
    fullWidth = false,
    countryCodes,
    report = 'analytics',
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const { translate } = useTranslate();
    const [loading, setLoading] = useState(false);
    const disabled = !serviceProviderId || !hasValidDateRange || !fromDate || !toDate;

    const handleDownload = async () => {
        if (disabled || loading || !fromDate || !toDate) {
            return;
        }
        const from = format(fromDate, 'yyyy-MM-dd');
        const to = format(toDate, 'yyyy-MM-dd');
        const providerId = Number(serviceProviderId);
        setLoading(true);
        try {
            const response =
                report === 'serviceProviderBilling'
                    ? await downloadBillingReport({
                          service_provider_id: providerId,
                          from,
                          to,
                          ...(countryCodes?.length ? { country_codes: countryCodes } : {}),
                      })
                    : await downloadAdminAnalyticsReport(String(serviceProviderId), from, to);
            const blob =
                response.data instanceof Blob
                    ? response.data
                    : new Blob([response.data], { type: XLSX_MIME_TYPE });
            downloadBlob(
                blob,
                getReportFilename(
                    response.headers['content-disposition'],
                    report === 'serviceProviderBilling'
                        ? `Aliro-billing-${providerId}.xlsx`
                        : `Report ${from} - ${to}.xlsx`
                )
            );
        } catch (error) {
            const reportError =
                report === 'serviceProviderBilling' ? await withParsedBlobError(error) : error;
            dispatch(
                setAlert({
                    message: getErrorMessage(
                        reportError,
                        translate('financialReportDownloadFailed')
                    ),
                    type: 'error',
                })
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <SubmitButton
            type="button"
            loading={loading}
            disabled={disabled}
            onClick={handleDownload}
            fullWidth={fullWidth}
            sx={{ height: 40, whiteSpace: 'nowrap' }}
        >
            {loading ? 'downloading' : 'downloadFinancialReport'}
        </SubmitButton>
    );
};

export default FinancialReportDownloadButton;
